import {
  Box,
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Select,
  Text,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

const MotionBox = motion(Box);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'beta',
    message: '',
  });

  const toast = useToast();
  const { trackEvent } = useAnalytics();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    trackEvent('contact_form_submitted', {
      interest_type: formData.type,
      has_message: formData.message.length > 0,
    });

    toast({
      title: 'Message sent!',
      description:
        formData.type === 'beta'
          ? "Thank you for your interest in our beta program! We'll be in touch soon with next steps."
          : "We'll get back to you soon to discuss how we can work together.",
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    setFormData({ name: '', email: '', type: 'beta', message: '' });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    trackEvent('contact_type_changed', {
      from: formData.type,
      to: newType,
    });
    setFormData({ ...formData, type: newType });
  };

  return (
    <Box
      id='contact'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      bgGradient='linear(to-b, brand.dark, rgba(11, 61, 145, 0.2))'
      onMouseEnter={() => trackEvent('section_viewed', { section: 'contact' })}>
      <Container maxW='800px' px={{ base: 4, md: 6 }}>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}>
          <VStack spacing={{ base: 6, md: 8 }}>
            <Heading
              as='h2'
              size={{ base: 'lg', md: 'xl' }}
              textAlign='center'
              color='brand.light'
              letterSpacing='wider'>
              Join Our Journey
            </Heading>
            <Text
              color='gray.300'
              fontSize={{ base: 'md', md: 'lg' }}
              textAlign='center'
              px={{ base: 2, md: 0 }}>
              Whether you're interested in our beta program, potential partnerships, or just want to
              learn more about our vision, we'd love to hear from you.
            </Text>
            <Box
              as='form'
              onSubmit={handleSubmit}
              w='100%'
              p={{ base: 6, md: 8 }}
              borderRadius='xl'
              bg='rgba(11, 61, 145, 0.3)'
              border='1px solid'
              borderColor='brand.accent'>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color='brand.light'>Name</FormLabel>
                  <Input
                    type='text'
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color='brand.light'>Email</FormLabel>
                  <Input
                    type='email'
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color='brand.light'>I'm interested in...</FormLabel>
                  <Select
                    value={formData.type}
                    onChange={handleTypeChange}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}>
                    <option value='beta'>Joining the Beta Program</option>
                    <option value='partner'>Partnership Opportunities</option>
                    <option value='general'>Learning More</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color='brand.light'>Message</FormLabel>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    rows={5}
                    placeholder={
                      formData.type === 'beta'
                        ? "Tell us about your development workflow and what you'd like to see in our tools..."
                        : 'How can we help you?'
                    }
                    fontSize={{ base: 'sm', md: 'md' }}
                  />
                </FormControl>
                <Button
                  type='submit'
                  variant='primary'
                  size={{ base: 'md', md: 'lg' }}
                  width='full'
                  mt={4}
                  fontSize={{ base: 'sm', md: 'md' }}
                  height={{ base: '40px', md: 'auto' }}>
                  {formData.type === 'beta' ? 'Join Beta Program' : 'Send Message'}
                </Button>
              </VStack>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Contact;
